import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { SEOHead } from '../../components/SEOHead';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ArrowLeft } from 'lucide-react';

interface BlogData {
  id: string;
  title: string;
  intro: string;
  content: string;
  featured_image_url: string | null;
  published_at: string;
  updated_at: string;
  user_id: string;
  slug: string;
  share_count?: number;
  view_count?: number;
}

interface UserData {
  first_name: string;
  company_name: string;
  username_slug: string;
  id: string;
}

interface BlogSettings {
  logo_url: string | null;
  about_text: string;
  accent_color: string;
}

interface SEOData {
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  og_image: string | null;
  canonical_url: string;
}

export function BlogPost() {
  const { username, slug } = useParams<{ username: string; slug: string }>();
  const [loading, setLoading] = useState(true);
  const [blog, setBlog] = useState<BlogData | null>(null);
  const [user, setUser] = useState<UserData | null>(null);
  const [blogSettings, setBlogSettings] = useState<BlogSettings | null>(null);
  const [seo, setSeo] = useState<SEOData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBlogPost();
    incrementViewCount();
  }, [username, slug]);

  const loadBlogPost = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('first_name, company_name, username_slug, id')
        .eq('username_slug', username)
        .maybeSingle();

      if (userError || !userData) {
        setError('Gebruiker niet gevonden');
        setLoading(false);
        return;
      }

      setUser(userData);

      const { data: settingsData } = await supabase
        .from('user_blog_settings')
        .select('logo_url, about_text, accent_color')
        .eq('user_id', userData.id)
        .maybeSingle();

      if (settingsData) {
        setBlogSettings(settingsData);
      }

      const { data: blogData, error: blogError } = await supabase
        .from('published_blogs')
        .select('*')
        .eq('user_id', userData.id)
        .eq('slug', slug)
        .eq('status', 'published')
        .eq('is_active', true)
        .maybeSingle();

      if (blogError || !blogData) {
        setError('Blog niet gevonden');
        setLoading(false);
        return;
      }

      setBlog(blogData);

      const { data: seoData } = await supabase
        .from('blog_seo_metadata')
        .select('*')
        .eq('blog_id', blogData.id)
        .maybeSingle();

      if (seoData) {
        setSeo(seoData);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading blog:', err);
      setError('Er ging iets mis bij het laden van de blog');
      setLoading(false);
    }
  };

  const incrementViewCount = async () => {
    if (!username || !slug) return;

    try {
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('username_slug', username)
        .maybeSingle();

      if (!userData) return;

      const { data: blogData } = await supabase
        .from('published_blogs')
        .select('id, view_count')
        .eq('user_id', userData.id)
        .eq('slug', slug)
        .maybeSingle();

      if (blogData) {
        await supabase
          .from('published_blogs')
          .update({ view_count: (blogData.view_count || 0) + 1 })
          .eq('id', blogData.id);
      }
    } catch (err) {
      console.error('Error incrementing view count:', err);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF] flex items-center justify-center">
        <LoadingSpinner text="Blog laden..." />
      </div>
    );
  }

  if (error || !blog || !user) {
    return (
      <div className="min-h-screen bg-[#F2F2EF] flex items-center justify-center p-4">
        <div className="card max-w-md text-center">
          <h1 className="text-2xl font-bold text-[#231F20] mb-4">
            {error || 'Blog niet gevonden'}
          </h1>
          <Link to={`/blog/${username}`} className="btn-secondary">
            Terug naar overzicht
          </Link>
        </div>
      </div>
    );
  }

  const blogUrl = `https://lees-nu.nl/blog/${username}/${slug}`;

  return (
    <>
      <SEOHead
        title={seo?.meta_title || blog.title}
        description={seo?.meta_description || blog.intro}
        keywords={seo?.meta_keywords}
        image={seo?.og_image || blog.featured_image_url || undefined}
        url={blogUrl}
        type="article"
        publishedTime={blog.published_at}
        modifiedTime={blog.updated_at}
        author={user.company_name}
      />

      <div className="min-h-screen bg-[#F2F2EF]">
        <article className="max-w-4xl mx-auto px-4 py-8 md:py-12">
          <div className="flex flex-col items-center mb-8 md:mb-12">
            {blogSettings?.logo_url && (
              <Link to={`/blog/${username}`} className="mb-6">
                <img
                  src={blogSettings.logo_url}
                  alt={`${user.company_name} logo`}
                  className="max-h-[100px] max-w-[180px] w-auto h-auto object-contain"
                />
              </Link>
            )}
            <Link
              to={`/blog/${username}`}
              className="inline-flex items-center gap-2 text-gray-600 transition-colors hover:opacity-80"
              style={{ color: blogSettings?.accent_color || '#FF6B35' }}
            >
              <ArrowLeft className="w-4 h-4" />
              Terug naar overzicht
            </Link>
          </div>

          {blog.featured_image_url && (
            <img
              src={blog.featured_image_url}
              alt={blog.title}
              className="w-full h-64 md:h-96 object-cover rounded-xl mb-8 shadow-lg"
            />
          )}

          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#231F20] mb-6 leading-tight">
              {blog.title}
            </h1>

            <p className="text-lg md:text-xl text-gray-700 leading-relaxed font-medium">
              {blog.intro}
            </p>
          </header>

          <div className="prose prose-lg max-w-none">
            <div
              className="blog-content text-gray-800 text-lg"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />
          </div>

          <footer className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex justify-center">
              <Link
                to={`/blog/${username}`}
                className="btn-secondary"
              >
                Meer artikelen
              </Link>
            </div>
          </footer>
        </article>

        <div className="bg-white py-8 mt-12 border-t border-gray-200">
          <div className="max-w-4xl mx-auto px-4 text-center text-sm text-gray-600">
            <p>Mogelijk gemaakt door <a href="https://www.dibbi.nl" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: blogSettings?.accent_color || '#FF6B35' }}>dibbi</a></p>
          </div>
        </div>
      </div>
    </>
  );
}
